import { t } from 'i18next';

import { toast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';
import {
  DropdownState,
  PieceMetadataModel,
  PieceMetadataModelSummary,
  PiecePropertyMap,
} from '@activepieces/pieces-framework';
import {
  Action,
  ActionType,
  AddPieceRequestBody,
  GetPieceRequestParams,
  GetPieceRequestQuery,
  ListPiecesRequestQuery,
  PackageType,
  PieceOptionRequest,
  spreadIfDefined,
  SwaggerPieceRef,
  Trigger,
  TriggerType,
} from '@activepieces/shared';

import { PieceStepMetadata, StepMetadata } from './types';
import { pieceSelectorUtils } from '@/app/builder/pieces-selector/piece-selector-utils';
import { useTranslation } from 'react-i18next';

export const CORE_STEP_METADATA: Record<
  Exclude<ActionType, ActionType.PIECE> | TriggerType.EMPTY,
  StepMetadata
> = {
  [ActionType.CODE]: {
    displayName: t('Code'),
    logoUrl: 'https://cdn.activepieces.com/pieces/code.svg',
    description: t('Powerful Node.js & TypeScript code with npm'),
    type: ActionType.CODE as const,
  },
  [ActionType.LOOP_ON_ITEMS]: {
    displayName: t('Loop on Items'),
    logoUrl: 'https://cdn.activepieces.com/pieces/loop.svg',
    description: 'Iterate over a list of items',
    type: ActionType.LOOP_ON_ITEMS as const,
  },
  [ActionType.ROUTER]: {
    displayName: 'Router',
    logoUrl: 'https://cdn.activepieces.com/pieces/branch.svg',
    description: t('Split your flow into branches depending on condition(s)'),
    type: ActionType.ROUTER,
  },
  [TriggerType.EMPTY]: {
    displayName: t('Empty Trigger'),
    logoUrl: 'https://cdn.activepieces.com/pieces/empty-trigger.svg',
    description: t('Empty Trigger'),
    type: TriggerType.EMPTY as const,
  },
};

export const piecesApi = {
  extractExactVersion(version: string | undefined): string | undefined {
    if (!version) return undefined;
    return version.replace(/^[~^]/, '');
  },

  list(request: ListPiecesRequestQuery): Promise<PieceMetadataModelSummary[]> {
    return api.get<PieceMetadataModelSummary[]>('/v1/pieces', request);
  },
  get(
    request: GetPieceRequestParams & GetPieceRequestQuery,
  ): Promise<PieceMetadataModel> {
    return api.get<PieceMetadataModel>(`/v1/pieces/${request.name}`, {
      version: this.extractExactVersion(request.version),
    });
  },
  /**
   * Swagger pieces the current tenant is allowed to use. The endpoint used to
   * return the full OpenAPI document of every Swagger stored in the platform;
   * it now returns only the references belonging to this tenant.
   */
  getSwaggerPieces(): Promise<SwaggerPieceRef[]> {
    return api.get<SwaggerPieceRef[]>('/v1/pieces/swagger-objects');
  },
  options<T extends DropdownState<unknown> | PiecePropertyMap>(
    request: PieceOptionRequest,
  ): Promise<T> {
    return api.post<T>(`/v1/pieces/options`, request).catch((error) => {
      console.error(error);
      toast({
        title: t('Error'),
        description: t(
          'An internal error occured while fetching data, please contact support',
        ),
        variant: 'destructive',
      });
      return {
        options: [] as any[],
        disabled: true,
        placeholder: t('An internal error occured, please contact support'),
      } as T;
    });
  },
  mapToMetadata(
    type: 'action' | 'trigger',
    piece: PieceMetadataModelSummary | PieceMetadataModel,
  ): PieceStepMetadata {
    return {
      displayName: piece.displayName,
      logoUrl: piece.logoUrl,
      description: piece.description,
      type: type === 'action' ? ActionType.PIECE : TriggerType.PIECE,
      pieceType: piece.pieceType,
      pieceName: piece.name,
      pieceVersion: piece.version,
      categories: piece.categories ?? [],
      packageType: piece.packageType,
      auth: piece.auth,
    };
  },
  mapToSuggestions(
    piece: PieceMetadataModelSummary,
  ): Pick<PieceMetadataModelSummary, 'suggestedActions' | 'suggestedTriggers'> {
    return {
      suggestedActions: piece.suggestedActions,
      suggestedTriggers: piece.suggestedTriggers,
    };
  },
  async getMetadata(
    step: Action | Trigger,
    pieceModel?: PieceMetadataModel
  ): Promise<StepMetadata> {
    const customLogoUrl =
      'customLogoUrl' in step ? step.customLogoUrl : undefined;
    switch (step.type) {
      case ActionType.ROUTER:
      case ActionType.LOOP_ON_ITEMS:
      case ActionType.CODE:
      case TriggerType.EMPTY:
        return {
          ...CORE_STEP_METADATA[step.type],
          ...spreadIfDefined('logoUrl', customLogoUrl),
        };
      case ActionType.PIECE: {
        if (!pieceModel) {
          throw new Error('pieceModel is required for ActionType.PIECE steps');
        }
        const metadata = await piecesApi.mapToMetadata('action', pieceModel);
        return {
          ...metadata,
          ...spreadIfDefined('logoUrl', customLogoUrl),
        };
      }
      case TriggerType.PIECE: {
        if (!pieceModel) {
          throw new Error('pieceModel is required for TriggerType.PIECE steps');
        }
        const metadata = await piecesApi.mapToMetadata('trigger', pieceModel);
        return {
          ...metadata,
          ...spreadIfDefined('logoUrl', customLogoUrl),
        };
      }
    }
  },
  syncFromCloud() {
    return api.post<void>(`/v1/pieces/sync`, {});
  },
  async install(params: AddPieceRequestBody) {
    const formData = new FormData();
    formData.set('packageType', params.packageType);
    formData.set('pieceName', params.pieceName);
    formData.set('pieceVersion', params.pieceVersion);
    formData.set('scope', params.scope);
    if (params.packageType === PackageType.ARCHIVE) {
      const buffer = await (
        params.pieceArchive as unknown as File
      ).arrayBuffer();
      formData.append('pieceArchive', new Blob([buffer]));
    }

    return api.post<PieceMetadataModel>('/v1/pieces', formData, undefined, {
      'Content-Type': 'multipart/form-data',
    });
  },
  delete(id: string) {
    return api.delete(`/v1/pieces/${id}`);
  },
};
